// Pure decision for "is Aurora (the dashboard) available to this user?".
// React-free so it can be unit tested; the hook wrapper is use-aurora-enabled.ts.

/**
 * Accounts created at or after this instant always get Aurora, regardless of
 * the `aurora_enabled` PostHog rollout.
 *
 * The onboarding wizard was removed (#1165) and new signups now land in the
 * dashboard/assistant, so the classic console is no longer a coherent first
 * experience — a brand-new user must not be able to fall outside the rollout.
 * Accounts created before the cutoff keep following the flag.
 *
 * This lives in app code rather than as a `created_at` cohort rule in PostHog
 * (which already receives created_at at identify time — see use-me.ts) so the
 * guarantee for new signups doesn't depend on a dashboard setting or on PostHog
 * being reachable. The trade-off: PostHog's own value for `aurora_enabled`, and
 * so the `$feature/aurora_enabled` property it stamps on captured events, can
 * read false for a post-cutoff user.
 *
 * Retire the cutoff together with the flag once every pre-cutoff account is on
 * Aurora — until then it has no expiry.
 */
export const AURORA_ALL_SIGNUPS_CUTOFF = "2026-08-07T00:00:00.000Z"

const CUTOFF_MS = Date.parse(AURORA_ALL_SIGNUPS_CUTOFF)

export function isPostAuroraCutoffSignup(createdAt: Date | undefined): boolean {
  if (createdAt == null) return false
  return createdAt.getTime() >= CUTOFF_MS
}

// Tri-state OR over the two ways Aurora can be on. `undefined` means "not known
// yet, don't answer" — a premature `false` bounces a brand-new user out of the
// dashboard (dashboard-auth-gate) and into the classic console.
export function resolveAuroraEnabled(
  flagValue: boolean | undefined,
  isPostCutoffSignup: boolean | undefined
): boolean | undefined {
  if (flagValue === true || isPostCutoffSignup === true) return true
  if (flagValue === undefined || isPostCutoffSignup === undefined) {
    return undefined
  }
  return false
}
